import { motion } from 'framer-motion'
import React from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'
import { Button, TextInput } from '@demandlocal/components'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from './CreateNewCampaignForm'
import { ReactComponent as DollarSign } from '../../../images/icons/dollar_sign.svg'
import { ReactComponent as CarIcon } from '../../../images/icons/car_round.svg'
import { ReactComponent as AnnouncementIcon } from '../../../images/icons/announcement_round.svg'
import { ReactComponent as PeopleIcon } from '../../../images/icons/audience_people_round.svg'
import CampaignExampleCard from '../../../components/Campaigns/CampaignExampleCard'
import { isEmpty, take, upperCase } from 'lodash'
import { useCreateInventoryCampaign } from '../../../api/dealership/campaigns/useCreateInventoryCampaign'
import { useDealership } from '../../../store/zustand/dealershipStore'
import { useEditLinkCampaign } from '../../../api/dealership/campaigns/useEditLinkCampaign'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import { formatNumber } from '../../../lib/utils/formatNumber'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'
import classNames from 'classnames'
import { queryClient } from '../../../api/queryClient'

type Props = {
  step: number
  setStep: (step: number) => void
}

const CreateNewCampaignReview = ({ step, setStep }: Props): JSX.Element => {
  const { handleSubmit, watch } = useFormContext()
  const dealership = useDealership()
  const [containerHeight, setContainerHeight] = React.useState(0)
  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { mutateAsync: createAsanaTicket } = useCreateInventoryCampaign()
  const { mutateAsync: saveLinkCampaign, isLoading } = useEditLinkCampaign()

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Saving campaign')
    const vendor_campaign_name = `${dealership?.dealership} - ${
      formValues.campaign_name
    } [${upperCase(formValues?.product?.product_code)}${formValues.pacing_id}]`
    const response = await createAsanaTicket({
      data: {
        vendor_campaign_name: vendor_campaign_name,
        notes: formValues?.notes,
        budget: formValues.budget,
        zip: formValues.target?.zip,
        product: formValues.product?.product_name,
        is_active: 1,
        allowed_vehicle_states: formValues.inventory?.details?.allowed_vehicle_states,
        inventory_data_providers: formValues.inventory?.details?.inventory_data_providers,
        inventory_data_provider_clients:
          formValues.inventory?.details?.inventory_data_provider_clients,
        exclude_if_no_price: formValues.inventory?.filters?.exclude_if_no_price,
        no_price_only: formValues.inventory?.filters?.no_price_only,
        allowed_makes: formValues.inventory?.filters?.allowed_makes,
        disallowed_makes: formValues.inventory?.filters?.disallowed_makes,
        allowed_models: formValues.inventory?.filters?.allowed_models,
        disallowed_models: formValues.inventory?.filters?.disallowed_models,
        allowed_body_substrings: formValues.inventory?.filters?.allowed_body_substrings,
        disallowed_body_substrings:
          formValues.inventory?.filters?.disallowed_body_substrings,
        earliest_allowed_year: formValues.inventory?.filters?.earliest_allowed_year,
        latest_allowed_year: formValues.inventory?.filters?.latest_allowed_year,
        lowest_allowed_price: formValues.inventory?.filters?.lowest_allowed_price,
        highest_allowed_price: formValues.inventory?.filters?.highest_allowed_price,
        lowest_allowed_mileage: formValues.inventory?.filters?.lowest_allowed_mileage,
        highest_allowed_mileage: formValues.inventory?.filters?.highest_allowed_mileage,
        allowed_stock_number_prefixes:
          formValues.inventory?.filters?.allowed_stock_number_prefixes,
        disallowed_stock_number_prefixes:
          formValues.inventory?.filters?.disallowed_stock_number_prefixes,
        minimum_age_days: formValues.inventory?.filters?.minimum_age_days,
        maximum_age_days: formValues.inventory?.filters?.maximum_age_days,
        allowed_locations: formValues.inventory?.filters?.allowed_locations,
        disallowed_locations: formValues.inventory?.filters?.disallowed_locations,
      },
    })

    if (formValues.campaign_id) {
      await saveLinkCampaign({
        data: {
          vendor_campaign_name: vendor_campaign_name,
          notes: values?.notes,
          is_incomplete: 0,
        },
        link_campaign_id: formValues.campaign_id,
      })
    }

    toast.success('Campaign saved!', { id: toastId })
    await queryClient.refetchQueries([
      'dealership',
      dealership?.id,
      'campaigns',
      'incomplete',
    ])

    await queryClient.refetchQueries(['dealership', dealership?.id, 'campaigns', 'list'])
    setStep(step + 1)
  }

  const filterDisplayNames = {
    allowed_makes: 'Allowed makes',
    disallowed_makes: 'Disallowed makes',
    allowed_models: 'Allowed models',
    disallowed_models: 'Disallowed models',
    lowest_allowed_price: 'Minimum price',
    highest_allowed_price: 'Max price',
    no_price_only: 'Only cars with no price',
    exclude_if_no_price: 'Exclude cars with no price',
    lowest_allowed_mileage: 'Minimum mileage',
    highest_allowed_mileage: 'Max mileage',
    allowed_stock_number_prefixes: 'Allowed stock number prefixes',
    disallowed_stock_number_prefixes: 'Disllowed stock number prefixes',
    minimum_age_days: 'Minimum days on lot',
    maximum_age_days: 'Max days on lot',
  }
  const filters: Array<Record<string, string | boolean>> = []

  if (formValues.inventory?.filters) {
    filters.push({
      filterName: 'Allowed states',
      value:
        formValues?.inventory?.details?.allowed_vehicle_states?.replaceAll(',', ', ') ??
        'None',
    })
    for (const [key, value] of Object.entries(formValues.inventory.filters)) {
      const filterName = filterDisplayNames[key as keyof typeof filterDisplayNames]
      if (value) filters.push({ filterName, value })
    }
  }

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className={'flex flex-1 flex-col overflow-auto pb-20'}>
      <motion.div
        ref={(_ref) => {
          if (containerHeight === 0 && _ref?.clientHeight)
            setContainerHeight(_ref?.clientHeight ?? 0)
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={classNames('flex flex-1 flex-col pt-8 pb-8 overflow-auto', {
          'max-h-[auto]': containerHeight === 0,
          'max-h-full]': containerHeight !== 0,
        })}>
        {containerHeight > 0 && formValues?.product && (
          <div className={'flex flex-col gap-5 px-12'}>
            <span className={'pb-5'}>
              You can revise all your steps here before launching
            </span>
            <span className={'font-bold'}>Your new campaign consists of:</span>
            <div className={'py-3 w-full border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3 w-full'}>
                  <span className={'self-start'}>
                    <DollarSign />
                  </span>
                  <div className={'flex flex-col gap-4 py-2 w-full'}>
                    <span className={'font-bold'}>Details</span>
                    <div className={'flex flex-row gap-5 w-full py-5'}>
                      <div
                        className={
                          'p-5 flex flex-col gap-3 rounded-xl w-1/4 bg-secondary-sunglow/20'
                        }>
                        <span className={'text-sm'}>Location</span>
                        {formValues?.target?.zip?.split(',').map((zip) => (
                          <span key={zip} className={'font-bold text-md'}>
                            {zip}
                          </span>
                        ))}
                      </div>
                      <div
                        className={
                          'p-5 flex flex-col gap-3 rounded-xl w-1/4 bg-blue-aero/20'
                        }>
                        <span className={'text-sm'}>Dates</span>
                        <span className={'font-bold text-sm'}>
                          {dayjs(formValues?.start_date).format('MMM D')} -{' '}
                          {dayjs(formValues?.end_date).format('MMM D, YYYY')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {formValues?.target?.use_custom_audience && (
              <div className={'py-3 w-full border-b-2'}>
                <div className={'flex flex-row gap-3 items-center justify-between'}>
                  <div className={'flex flex-row items-center gap-3'}>
                    <span className={'self-start'}>
                      <PeopleIcon />
                    </span>
                    <div className={'flex flex-col gap-4 py-2 w-full'}>
                      <span className={'font-bold'}>Marketing audience</span>
                      <div className={'grid grid-cols-4 gap-5 items-center'}>
                        {take(
                          formValues?.target?.audience.map((audience) => (
                            <div
                              key={audience.name}
                              className={
                                'flex flex-col gap-3 border-2 rounded-lg h-full col-span-1 items-center p-5'
                              }>
                              <span className={'text-xs text-center'}>
                                {audience.name}
                              </span>
                              <span className={'text-sm font-bold text-center'}>
                                Audience size: {formatNumber(Number(audience.size))}
                              </span>
                            </div>
                          )),
                          3,
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className={'py-3 w-full border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3'}>
                  <span className={'self-start'}>
                    <CarIcon />
                  </span>
                  <div className={'flex flex-col gap-4 py-2 font-bold'}>
                    Cars to promote
                    <div
                      className={
                        'my-4 flex flex-col gap-3 border-2 p-4 rounded-lg w-fit  font-bold text-sm'
                      }>
                      {isEmpty(filters)
                        ? 'All cars'
                        : filters.map(({ filterName, value }) => {
                            return (
                              <div
                                key={`${filterName}-${value}`}
                                className={'flex flex-col gap-3'}>
                                {filterName} : {value == true ? 'Yes' : value}
                              </div>
                            )
                          })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={'py-3 w-full border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3'}>
                  <span className={'self-start'}>
                    <AnnouncementIcon />
                  </span>
                  <div className={'flex flex-col gap-4 py-2'}>
                    <span className="font-bold">Promote using</span>
                    <div
                      className={
                        'flex flex-col items-center border-2 p-4 rounded-lg w-1/4 gap-3 text-center'
                      }>
                      {formValues.product?.icon}
                      <span className={'font-bold'}>
                        {formValues.product?.product_name}
                      </span>
                      <span className={'text-xs'}>
                        {formValues.product?.product_description}
                      </span>
                      <span className={'text-xs font-bold'}>
                        Budget: ${formatNumber(Number(formValues?.budget))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={'w-full py-8 border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3 w-full'}>
                  <span className={'self-start'}>
                    <PeopleIcon />
                  </span>
                  <div className={'flex flex-col gap-4 py-2'}>
                    <span className={'font-bold'}>Example ads</span>
                    <div className={'flex flex-row gap-5 w-full py-5'}>
                      <div
                        key={formValues.product?.product_code}
                        className={
                          'flex flex-col items-center border-2 p-4 rounded-lg w-1/2 font-bold gap-3 text-center'
                        }>
                        {formValues?.product?.product_code === 'vla' ||
                        formValues?.product?.product_code === 'inv' ? (
                          <CampaignExampleCard
                            url="images/campaign/campaign_example1.png"
                            title="Inventory search"
                            textSize="text-xs"
                            body=""
                          />
                        ) : formValues?.product?.product_code === 'dyn' ? (
                          <CampaignExampleCard
                            url="images/campaign/campaign_example3.png"
                            title="Facebook Dynamic"
                            textSize="text-xs"
                            body=""
                          />
                        ) : (
                          <CampaignExampleCard
                            url="images/campaign/campaign_example2.png"
                            title="Dynamic display"
                            textSize="text-xs"
                            body=""
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={'w-full py-8 border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3 w-full'}>
                  <span className={'self-start'}>
                    <PeopleIcon />
                  </span>
                  <div className={'flex flex-col gap-4 py-2 w-full'}>
                    <span className={'font-bold'}>Additional notes</span>
                    <div className={'flex flex-row gap-5 w-full py-5'}>
                      <TextInput inverted={true} label={''} name={`notes`} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
      <motion.div
        layout
        layoutId={'buttons-container'}
        className={
          'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full'
        }>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            setStep(step - 1)
          }}>
          Back
        </Button>
        <CampaignProgressBar step={step} />
        <Button variant={'primary'} disabled={isLoading}>
          Continue
        </Button>
      </motion.div>
    </motion.form>
  )
}

export default CreateNewCampaignReview
